import React from 'react';
import {
  Menu,
  MenuItem,
  type MenuProps,
  type MenuItemProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Check } from 'lucide-react';

const ROUNDED_MENU_PAPER_SX = {
  borderRadius: '16px',
  boxShadow: '0 14px 40px rgba(15,23,41,0.16)',
  overflow: 'hidden',
};

const ROUNDED_MENU_ITEM_SX = {
  fontSize: '14px',
  color: '#0F1729',
  mx: 0.75,
  my: '0.25rem',
  position: 'relative',
  // Reserve room on the right for the (absolutely-positioned) selected check.
  pr: '40px',
  borderRadius: '20px',
  '&:hover': { backgroundColor: '#f5f6f7' },
  '&.Mui-selected': { backgroundColor: '#f9fafb' },
  '&.Mui-selected:hover': { backgroundColor: '#f9fafb' },
};

export interface RoundedMenuProps extends Omit<MenuProps, 'slotProps'> {
  /** Minimum menu width. Defaults to 200. */
  minWidth?: number;
  /** Extra paper sx (e.g. a top offset). */
  paperSx?: SxProps<Theme>;
}

/**
 * RoundedMenu
 *
 * Atomic level: Molecule.
 *
 * A MUI Menu with a rounded, soft-shadow, elevation-0 paper. Forwards every Menu
 * prop (anchor, focus config, …); zero domain knowledge.
 */
export function RoundedMenu({ minWidth = 200, paperSx, children, ...menuProps }: RoundedMenuProps) {
  return (
    <Menu
      {...menuProps}
      slotProps={{
        paper: {
          elevation: 0,
          sx: [ROUNDED_MENU_PAPER_SX, { minWidth }, ...(Array.isArray(paperSx) ? paperSx : [paperSx])],
        },
      }}
    >
      {children}
    </Menu>
  );
}

export type RoundedMenuItemProps = MenuItemProps;

/**
 * RoundedMenuItem
 *
 * A MenuItem with the inset pill-hover styling and a right-aligned check when
 * `selected`. Zero domain knowledge.
 */
export function RoundedMenuItem({ children, selected, sx, ...rest }: RoundedMenuItemProps) {
  return (
    <MenuItem selected={selected} sx={[ROUNDED_MENU_ITEM_SX, ...(Array.isArray(sx) ? sx : [sx])]} {...rest}>
      {children}
      {selected && (
        <Check size={14} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} />
      )}
    </MenuItem>
  );
}

export default RoundedMenu;
